import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Phone, 
  Settings, 
  Key, 
  Link, 
  CheckCircle2, 
  ExternalLink,
  Copy,
  AlertTriangle,
  Monitor,
  Smartphone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const WEBHOOK_URL = "https://kiprllcwlijaqzvclalu.functions.supabase.co/functions/v1/twilio-incoming-webhook";
const VOICE_URL = "https://kiprllcwlijaqzvclalu.supabase.co/functions/v1/twilio-voice/outbound-twiml";

export default function TwilioSetupGuide() {
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié!`);
  };

  return (
    <div className="container max-w-4xl py-8 space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Phone className="h-8 w-8 text-primary" />
          Configuration Twilio Voice
        </h1>
        <p className="text-muted-foreground text-lg">
          Guide complet pour configurer les appels entrants et sortants avec Twilio
        </p>
      </div>

      {/* Prerequisites */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-500" />
            Prérequis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline">1</Badge>
              <span>Compte Twilio actif avec un numéro de téléphone</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">2</Badge>
              <span>Account SID et Auth Token (dans Console → Account Info)</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">3</Badge>
              <span>Secrets configurés dans Lovable Cloud</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for different setups */}
      <Tabs defaultValue="incoming" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="incoming">Appels entrants</TabsTrigger>
          <TabsTrigger value="outgoing">Appels sortants</TabsTrigger>
          <TabsTrigger value="webrtc">WebRTC (Navigateur)</TabsTrigger>
        </TabsList>

        {/* Incoming Calls */}
        <TabsContent value="incoming" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Étape 1: Configurer le Webhook Voice</CardTitle>
              <CardDescription>
                Dans la console Twilio, configurez votre numéro pour envoyer les appels à notre webhook
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm font-medium">URL du Webhook (Voice)</p>
                <div className="flex gap-2">
                  <code className="flex-1 p-3 bg-muted rounded-lg text-sm break-all">
                    {WEBHOOK_URL}
                  </code>
                  <Button 
                    size="icon" 
                    variant="outline"
                    onClick={() => copyToClipboard(WEBHOOK_URL, "URL")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <Alert>
                <Settings className="h-4 w-4" />
                <AlertTitle>Configuration dans Twilio Console</AlertTitle>
                <AlertDescription className="space-y-2">
                  <ol className="list-decimal list-inside space-y-1 mt-2">
                    <li>Allez dans <strong>Phone Numbers → Manage → Active Numbers</strong></li>
                    <li>Cliquez sur votre numéro</li>
                    <li>Dans la section <strong>"Voice Configuration"</strong></li>
                    <li>Sous "A call comes in", sélectionnez <strong>"Webhook"</strong></li>
                    <li>Collez l'URL ci-dessus</li>
                    <li>Méthode HTTP: <strong>POST</strong></li>
                    <li>Cliquez sur <strong>"Save configuration"</strong></li>
                  </ol>
                </AlertDescription>
              </Alert>

              <Button variant="outline" className="w-full" asChild>
                <a 
                  href="https://console.twilio.com/us1/develop/phone-numbers/manage/incoming" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Ouvrir Twilio Console
                  <ExternalLink className="h-4 w-4 ml-2" />
                </a>
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Étape 2: Comportement des appels entrants</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 border rounded-lg space-y-2">
                  <div className="flex items-center gap-2">
                    <Monitor className="h-5 w-5 text-blue-500" />
                    <span className="font-medium">Mode Navigateur</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    L'appel est routé vers le client WebRTC de l'agent connecté à la plateforme
                  </p>
                </div>
                <div className="p-4 border rounded-lg space-y-2">
                  <div className="flex items-center gap-2">
                    <Smartphone className="h-5 w-5 text-green-500" />
                    <span className="font-medium">Mode Téléphone</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    L'appel est transféré au numéro de téléphone personnel de l'agent
                  </p>
                </div>
              </div>

              <Alert variant="default">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Routage intelligent</AlertTitle>
                <AlertDescription>
                  Le système trouve automatiquement un agent disponible. Si aucun agent n'est disponible,
                  l'appelant peut laisser un message vocal.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Outgoing Calls */}
        <TabsContent value="outgoing" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Appels sortants</CardTitle>
              <CardDescription>
                Les appels sortants fonctionnent automatiquement avec les secrets configurés
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <p className="font-medium">Secrets requis:</p>
                <div className="grid gap-2">
                  <div className="flex items-center gap-2 p-2 bg-muted rounded">
                    <Key className="h-4 w-4 text-muted-foreground" />
                    <code>TWILIO_ACCOUNT_SID</code>
                    <Badge variant="secondary">Configuré ✓</Badge>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-muted rounded">
                    <Key className="h-4 w-4 text-muted-foreground" />
                    <code>TWILIO_AUTH_TOKEN</code>
                    <Badge variant="secondary">Configuré ✓</Badge>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-muted rounded">
                    <Key className="h-4 w-4 text-muted-foreground" />
                    <code>TWILIO_PHONE_NUMBER</code>
                    <Badge variant="secondary">Configuré ✓</Badge>
                  </div>
                </div>
              </div>

              <Alert>
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <AlertTitle>Prêt à utiliser</AlertTitle>
                <AlertDescription>
                  Vous pouvez passer des appels sortants depuis le centre d'appels.
                  Le numéro affiché sera votre numéro Twilio.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WebRTC Setup */}
        <TabsContent value="webrtc" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Configuration WebRTC (Appels dans le navigateur)</CardTitle>
              <CardDescription>
                Permet aux agents de recevoir et passer des appels directement depuis leur navigateur
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant="default">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Configuration avancée requise</AlertTitle>
                <AlertDescription>
                  Le WebRTC nécessite une TwiML App et des API Keys spécifiques.
                </AlertDescription>
              </Alert>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Étape 1: Créer une TwiML App</h4>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                    <li>Allez dans <strong>Twilio Console → Voice → TwiML Apps</strong></li>
                    <li>Cliquez sur <strong>"Create new TwiML App"</strong></li>
                    <li>Nom: <code>Biznex Voice App</code></li>
                    <li>Voice Request URL:</li>
                  </ol>
                  <div className="flex gap-2 mt-2">
                    <code className="flex-1 p-2 bg-muted rounded text-xs break-all">
                      {VOICE_URL}
                    </code>
                    <Button 
                      size="icon" 
                      variant="outline"
                      onClick={() => copyToClipboard(VOICE_URL, "URL")}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Copiez le <strong>TwiML App SID</strong> (commence par AP...)
                  </p>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Étape 2: Créer une API Key</h4>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                    <li>Allez dans <strong>Account → API Keys & Tokens</strong></li>
                    <li>Cliquez sur <strong>"Create API Key"</strong></li>
                    <li>Type: <strong>Standard</strong></li>
                    <li>Nom: <code>Biznex Voice Key</code></li>
                    <li>Copiez le <strong>SID</strong> et le <strong>Secret</strong></li>
                  </ol>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Étape 3: Secrets à configurer</h4>
                  <div className="grid gap-2">
                    <div className="flex items-center gap-2 p-2 bg-muted rounded">
                      <Key className="h-4 w-4 text-muted-foreground" />
                      <code>TWILIO_API_KEY</code>
                      <Badge variant="secondary">Configuré ✓</Badge>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-muted rounded">
                      <Key className="h-4 w-4 text-muted-foreground" />
                      <code>TWILIO_API_SECRET</code>
                      <Badge variant="secondary">Configuré ✓</Badge>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-muted rounded">
                      <Key className="h-4 w-4 text-muted-foreground" />
                      <code>TWILIO_TWIML_APP_SID</code>
                      <Badge variant="secondary">Configuré ✓</Badge>
                    </div>
                  </div>
                </div>
              </div>

              <Button variant="outline" className="w-full" asChild>
                <a 
                  href="https://console.twilio.com/us1/develop/voice/manage/twiml-apps" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Ouvrir TwiML Apps
                  <ExternalLink className="h-4 w-4 ml-2" />
                </a>
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Testing */}
      <Card>
        <CardHeader>
          <CardTitle>Tester la configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Button variant="outline" asChild>
              <a href="/staff/calls">
                <Phone className="h-4 w-4 mr-2" />
                Centre d'appels Staff
              </a>
            </Button>
            <Button variant="outline" asChild>
              <a href="/admin/call-center">
                <Settings className="h-4 w-4 mr-2" />
                Centre d'appels Admin
              </a>
            </Button>
          </div>

          <Alert>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            <AlertTitle>Test recommandé</AlertTitle>
            <AlertDescription>
              <ol className="list-decimal list-inside space-y-1 mt-2">
                <li>Connectez-vous comme Staff</li>
                <li>Accédez au centre d'appels</li>
                <li>Configurez vos paramètres (navigateur ou téléphone)</li>
                <li>Passez en statut "Disponible"</li>
                <li>Appelez votre numéro Twilio depuis un autre téléphone</li>
              </ol>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
}
